import { useEffect, useState } from "react";
import { Robot, X } from "@phosphor-icons/react";

/**
 * ElevenLabs Conversational AI widget + a small dismiss/restore control.
 * The user can hide the "Need help?" popover with the tiny X — a compact "AI"
 * pill then sits in the corner and re-opens the widget on tap. The choice is
 * remembered in sessionStorage so it doesn't reappear during the same visit.
 *
 * Docs: https://elevenlabs.io/docs/conversational-ai/customization/widget
 */
const WIDGET_SCRIPT_ID = "elevenlabs-convai-widget-script";
const WIDGET_SRC = "https://unpkg.com/@elevenlabs/convai-widget-embed";
const HIDDEN_KEY = "tf-ai-assistant-hidden";

export default function AiAssistant({
  agentId = "agent_6401kynmfw2ze9es07c7qfxry1s7",
  label = "AI Tax Assistant",
}) {
  const [hidden, setHidden] = useState(false);

  // Restore preference for this session.
  useEffect(() => {
    try { setHidden(sessionStorage.getItem(HIDDEN_KEY) === "1"); } catch { /* private mode */ }
  }, []);

  // Inject widget bootstrap script exactly once per SPA session.
  useEffect(() => {
    if (document.getElementById(WIDGET_SCRIPT_ID)) return;
    const script = document.createElement("script");
    script.id = WIDGET_SCRIPT_ID;
    script.src = WIDGET_SRC;
    script.async = true;
    script.type = "text/javascript";
    document.body.appendChild(script);
  }, []);

  const hide = () => {
    setHidden(true);
    try { sessionStorage.setItem(HIDDEN_KEY, "1"); } catch { /* ignore */ }
  };
  const show = () => {
    setHidden(false);
    try { sessionStorage.removeItem(HIDDEN_KEY); } catch { /* ignore */ }
  };

  if (hidden) {
    return (
      <button
        type="button"
        onClick={show}
        aria-label="Reopen AI Tax Assistant"
        data-testid="ai-assistant-restore"
        className="no-print fixed bottom-4 right-4 z-[60] h-11 w-11 grid place-items-center rounded-full bg-[#0A2540] text-white shadow-lg hover:bg-[#0A2540]/90 active:scale-95 transition-transform"
      >
        <Robot size={22} weight="fill" />
      </button>
    );
  }

  return (
    <>
      {/* Small close button — sits above the widget's own launcher. */}
      <button
        type="button"
        onClick={hide}
        aria-label="Hide AI assistant"
        data-testid="ai-assistant-hide"
        className="no-print fixed bottom-24 right-4 sm:bottom-28 sm:right-6 z-[60] h-7 w-7 grid place-items-center rounded-full bg-white text-slate-700 border border-slate-300 shadow-md hover:bg-slate-100 hover:text-[#D32F2F] active:scale-95 transition-transform"
      >
        <X size={13} weight="bold" />
      </button>
      <elevenlabs-convai
        agent-id={agentId}
        action-text={label}
        start-call-text="Talk to Tax Assistant"
        listening-text="Listening…"
        speaking-text="Assistant speaking…"
        data-testid="ai-tax-assistant"
      ></elevenlabs-convai>
    </>
  );
}
